import { useState } from 'react';
import type { ChatThread } from '@/shared/types';

// The collapsible "previous chats" panel: select, rename (inline), fork,
// delete. Rendered in place of the transcript when the switcher is open.
export function ChatList({
  chats,
  activeChatId,
  onSelect,
  onRename,
  onDelete,
  onFork,
}: {
  chats: ChatThread[];
  activeChatId: string | null;
  onSelect: (id: string) => void;
  onRename: (id: string, title: string) => void;
  onDelete: (id: string) => void;
  onFork: (id: string) => void;
}) {
  const [editingId, setEditingId] = useState<string | null>(null);
  const [draft, setDraft] = useState('');

  function startRename(c: ChatThread) {
    setEditingId(c.id);
    setDraft(c.title);
  }
  function commitRename() {
    if (editingId) {
      const t = draft.trim();
      if (t) onRename(editingId, t);
    }
    setEditingId(null);
  }

  if (chats.length === 0) {
    return (
      <div className="flex-1 overflow-y-auto p-4 text-sm text-slate-500">
        No chats yet — start a new one.
      </div>
    );
  }

  return (
    <ul className="flex-1 divide-y divide-slate-200 overflow-y-auto">
      {chats.map((c) => (
        <li
          key={c.id}
          className={`group flex items-center gap-1 px-3 py-2 ${
            c.id === activeChatId ? 'bg-blue-50 dark:bg-blue-900/40' : 'hover:bg-slate-50'
          }`}
        >
          {editingId === c.id ? (
            <input
              autoFocus
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onBlur={commitRename}
              onKeyDown={(e) => {
                if (e.key === 'Enter') commitRename();
                else if (e.key === 'Escape') setEditingId(null);
              }}
              className="min-w-0 flex-1 rounded border border-slate-300 px-1.5 py-0.5 text-sm"
            />
          ) : (
            <button
              onClick={() => onSelect(c.id)}
              className="min-w-0 flex-1 text-left"
              title={c.title}
            >
              <div className="truncate text-sm text-slate-800">{c.title || 'New chat'}</div>
              <div className="text-[10px] text-slate-400">{relativeTime(c.updated_at)}</div>
            </button>
          )}
          {editingId !== c.id && (
            // Hover-revealed on desktop; touch devices have no hover, so keep
            // the actions always visible there (and when focused via keyboard).
            <div className="flex shrink-0 items-center gap-0.5 opacity-0 group-hover:opacity-100 group-focus-within:opacity-100 [@media(hover:none)]:opacity-100">
              <button
                onClick={() => onFork(c.id)}
                title="Fork to a new chat"
                className="rounded px-1 py-0.5 text-xs text-slate-500 hover:bg-slate-200"
              >
                ⑂
              </button>
              <button
                onClick={() => startRename(c)}
                title="Rename"
                className="rounded px-1 py-0.5 text-xs text-slate-500 hover:bg-slate-200"
              >
                ✎
              </button>
              <button
                onClick={() => {
                  if (confirm(`Delete chat "${c.title || 'New chat'}"? This can't be undone.`)) {
                    onDelete(c.id);
                  }
                }}
                title="Delete"
                className="rounded px-1 py-0.5 text-xs text-slate-500 hover:bg-red-100 hover:text-red-700"
              >
                🗑
              </button>
            </div>
          )}
        </li>
      ))}
    </ul>
  );
}

function relativeTime(ms: number): string {
  const s = Math.max(0, Math.round((Date.now() - ms) / 1000));
  if (s < 60) return 'just now';
  const m = Math.round(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.round(h / 24);
  return `${d}d ago`;
}
